import { moduleRoutes, standaloneModuleRoutes } from './lib/moduleRegistry';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { ADMIN_PAGE_ROLES } from './config/roles';
import { AppLayoutSkeleton, PageSkeleton } from './components/PageSkeleton';
import { fleetRoutes } from './pages/fleet/routes';

// Lazy load all pages and layout for code splitting
const AppLayout = lazy(() => import('./components/AppLayout').then((m) => ({ default: m.AppLayout })));
const Login = lazy(() => import('./pages/login/Login'));
const Register = lazy(() => import('./pages/register/Register'));
const VerifyEmail = lazy(() => import('./pages/verify-email/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/forgot-password/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/reset-password/ResetPassword'));

const Profile = lazy(() => import('./pages/profile/Profile'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Counters = lazy(() => import('./pages/counters/Counters'));
const Users = lazy(() => import('./pages/users/Users'));
const Invitation = lazy(() => import('./pages/invitation/Invitation'));
const ActivityHistory = lazy(() => import('./pages/activity-history/ActivityHistory'));

// Dispatch module
const DispatchBoard = lazy(() => import('./pages/dispatch/board/DispatchBoard'));
const DriveRecord = lazy(() => import('./pages/dispatch/drive/DriveRecord'));
const Planner = lazy(() => import('./pages/dispatch/planner/Planner'));
const FleetMap = lazy(() => import('./pages/dispatch/map/FleetMap'));
const Buyers = lazy(() => import('./pages/dispatch/buyers/Buyers'));
const BuyerDetail = lazy(() => import('./pages/dispatch/buyers/BuyerDetail'));
const Fleet = lazy(() => import('./pages/dispatch/fleet/Fleet'));
const TruckDetail = lazy(() => import('./pages/dispatch/fleet/TruckDetail'));
const Exceptions = lazy(() => import('./pages/dispatch/exceptions/Exceptions'));
const Documents = lazy(() => import('./pages/dispatch/documents/Documents'));
const DriverDetail = lazy(() => import('./pages/dispatch/fleet/DriverDetail'));
const Billing = lazy(() => import('./pages/dispatch/billing/Billing'));
const Outbox = lazy(() => import('./pages/dispatch/outbox/Outbox'));
const Roadmap = lazy(() => import('./pages/dispatch/roadmap/Roadmap'));
const PublicTracking = lazy(() => import('./pages/dispatch/track/PublicTracking'));

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<AppLayoutSkeleton />}>
              <AppLayout sideBarCollapsible={true}/>
            </Suspense>
          </ProtectedRoute>
        }
      >
        {/* Upstream-only modules (src/modules/*), see lib/moduleRegistry.ts */}
        {moduleRoutes.map((route) => {
          const page = <Suspense fallback={<PageSkeleton />}>{route.element}</Suspense>;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={route.roles ? <RoleBasedRoute allowedRoles={route.roles}>{page}</RoleBasedRoute> : page}
            />
          );
        })}
        <Route
          index
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DispatchBoard />
            </Suspense>
          }
        />
        <Route
          path="dispatch"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DispatchBoard />
            </Suspense>
          }
        />
        <Route
          path="dispatch/drives/:orderId"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DriveRecord />
            </Suspense>
          }
        />
        <Route
          path="dispatch/planner"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Planner />
            </Suspense>
          }
        />
        <Route
          path="dispatch/map"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <FleetMap />
            </Suspense>
          }
        />
        <Route
          path="dispatch/buyers"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Buyers />
            </Suspense>
          }
        />
        <Route
          path="dispatch/buyers/:id"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <BuyerDetail />
            </Suspense>
          }
        />
        <Route
          path="dispatch/fleet"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Fleet />
            </Suspense>
          }
        />
        <Route
          path="dispatch/fleet/trucks/:id"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <TruckDetail />
            </Suspense>
          }
        />
        <Route
          path="dispatch/exceptions"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Exceptions />
            </Suspense>
          }
        />
        <Route
          path="dispatch/documents"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Documents />
            </Suspense>
          }
        />
        <Route
          path="dispatch/fleet/drivers/:id"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DriverDetail />
            </Suspense>
          }
        />
        <Route
          path="dispatch/billing"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Billing />
            </Suspense>
          }
        />
        <Route
          path="dispatch/outbox"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Outbox />
            </Suspense>
          }
        />
        <Route
          path="dispatch/roadmap"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Roadmap />
            </Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Profile />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path="counters"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Counters />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <Users />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="activity-history"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <ActivityHistory />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        {/* Fleet admin (Contract D): every route behind the same admin guard */}
        {fleetRoutes()}
      </Route>
      {/* Upstream-only module pages with a layout of their own */}
      {standaloneModuleRoutes.map((route) => {
        const page = <Suspense fallback={<PageSkeleton minimal />}>{route.element}</Suspense>;
        return (
          <Route
            key={route.path}
            path={`/${route.path}`}
            element={
              <ProtectedRoute>
                {route.roles ? <RoleBasedRoute allowedRoles={route.roles}>{page}</RoleBasedRoute> : page}
              </ProtectedRoute>
            }
          />
        );
      })}
      <Route
        path="/track/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <PublicTracking />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ForgotPassword />
          </Suspense>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path="/register"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Register />
          </Suspense>
        }
      />
      <Route
        path="/verify-email/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <VerifyEmail />
          </Suspense>
        }
      />
      <Route
        path="/token/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Invitation />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
